import React from 'react';
import { AlertCircle, User, Clock, FileWarning } from 'lucide-react';
import { Button } from '../../atoms/Button';
import { type Report } from '../../../services/contentsService';

const REPORT_TYPES: Record<string, string> = {
  MA: 'Suça/Şiddete teşvik içeren medya',
  MCI: 'Telif ihlali olan medya (iç bağlantı)',
  MCO: 'Telif ihlali olan medya (dış bağlantı)',
  MD: 'Oynanmış/Sahte görüntü',
  MI: 'Nefret, küfür, hakaret içeren medya',
  MOB: 'Müstehcen',
  MOT: 'Diğer (medya için)',
  MS: 'Spam medya',
  TA: 'Suça/Şiddete teşvik içeren metin',
  TCI: 'Telif ihlali olan metin (iç bağlantı)',
  TCO: 'Telif ihlali olan metin (dış bağlantı)',
  TF: 'Yalan',
  TI: 'Nefret, küfür, hakaret içeren metin',
  TOT: 'Diğer (metin için)',
  TS: 'Spam metin',
};

export interface ReportsModalContentProps {
  reports: Report[];
  isLoading: boolean;
  contentTitle?: string;
  onClose: () => void;
}

export const ReportsModalContent = ({
  reports,
  isLoading,
  contentTitle,
  onClose,
}: ReportsModalContentProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mb-4"></div>
        <p className="text-gray-500">Raporlar yükleniyor...</p>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Rapor Bulunamadı</h3>
        <p className="text-gray-500">Bu içerik için henüz rapor bulunmamaktadır.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-3">
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>{reports.length}</strong> rapor bulundu
          </p>
        </div>
        {reports.map((report) => (
          <div
            key={report.id}
            className="border border-gray-200 rounded-xl p-5 hover:border-gray-300 hover:shadow-md transition-all bg-white"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileWarning className="w-5 h-5 text-red-500" />
                <span className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm font-semibold border border-red-200">
                  {REPORT_TYPES[report.type] || report.type}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock className="w-4 h-4" />
                <span>
                  {new Date(report.createdOn).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>

            {report.explanation && (
              <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 leading-relaxed">{report.explanation}</p>
              </div>
            )}

            {report.owner && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="w-4 h-4" />
                <span>
                  <strong>Raporlayan:</strong> {report.owner.username}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 flex justify-end">
        <Button
          variant="secondary"
          onClick={onClose}
          className="w-full sm:w-auto"
        >
          Kapat
        </Button>
      </div>
    </div>
  );
};
